import { useState } from 'react';
import type { Bulle, Maillon } from '../partage/api/types';
import {
  AU_PLUS_TOT_DU_PLAN,
  AUTRE_PROJET,
  ELLE_ATTEND,
  ELLE_LIBERE,
  NE_RETIENT_RIEN,
  nAutres,
  nChoses,
  parLaBulle,
  RETIENT,
} from '../partage/vocabulaire';
import { useConditionsPosees } from './Dependances';
import { bandeauDuMonde, dateDuPlan } from './ficheEnMots';
import './PlanDeLaFiche.css';

/** Au-delà de trois bulles dans une colonne, « + N autres », qui déplie. */
const MONTREES = 3;

interface NoeudDuPlan {
  cle: string;
  titre: string;
  /** Acquise : cochée ✓, en vert (maquette `.nd.ok`). */
  acquise: boolean;
  /** Sous le titre : « autre projet », « par « … » ». */
  mention: string | null;
}

/**
 * La fiche Plan (chantier types-de-fiche, modification 05 ; maquette du carnet
 * `fiche-quatre-types.html`, colonne « 4. Plan » : `.bandeau-monde`, `.schema`, `.col-s`, `.nd`,
 * `.milieu`, `.centre-n`, `.fl`, `.plus-n`, `.retient`). Les liens d'abord : en instruction, le
 * bandeau du monde ; puis ce qu'elle attend, la bulle, ce qu'elle libère, jusque dans les autres
 * projets ; enfin « Retient N choses · au plus tôt : … ».
 *
 * Rien ne se calcule ici : les conditions viennent de `useConditionsPosees` (comme le bloc
 * « Conditions »), « autre projet » du serveur (`autreProjet`), la date au plus tôt de la bulle.
 * « + Condition » est sous le schéma, toujours (chantier liens-entre-bulles, ticket 05).
 */
export function PlanDeLaFiche({ bulle, mere }: { bulle: Bulle; mere: Maillon | null }) {
  const { attend, retient, heritees } = useConditionsPosees(bulle, mere);
  const bandeau = bandeauDuMonde(bulle);

  const attendues: NoeudDuPlan[] = [
    ...attend.map((l) => ({
      cle: l.lienId,
      titre: l.titre,
      acquise: l.etat === 'fait',
      mention: l.autreProjet ? AUTRE_PROJET : null,
    })),
    ...heritees.map((r) => ({ cle: `herite-${r.id}`, titre: r.titre, acquise: false, mention: parLaBulle(r.via.titre) })),
  ];
  const liberees: NoeudDuPlan[] = retient.map((l) => ({
    cle: l.lienId,
    titre: l.titre,
    acquise: l.etat === 'fait',
    mention: l.autreProjet ? AUTRE_PROJET : null,
  }));
  const seule = attendues.length === 0 && liberees.length === 0;

  return (
    <section className="plan-fiche">
      {bandeau && (
        <p className="plan-bandeau">
          {bandeau.debut}
          {bandeau.depuis && (
            <>
              {' · '}
              <b>{bandeau.depuis}</b>
            </>
          )}
        </p>
      )}

      <div className={`plan-schema${seule ? ' plan-schema--seule' : ''}`}>
        {!seule && <ColonneDuPlan titre={ELLE_ATTEND} noeuds={attendues} />}
        <div className="plan-milieu">
          <div className="plan-noeud plan-centre">{bulle.titre}</div>
          {!seule && (
            <div className="plan-fleche" aria-hidden="true">
              →
            </div>
          )}
        </div>
        {!seule && <ColonneDuPlan titre={ELLE_LIBERE} noeuds={liberees} />}
      </div>

      <p className="plan-retient">
        {retient.length > 0 ? (
          <>
            {RETIENT} <b>{nChoses(retient.length)}</b>
          </>
        ) : (
          NE_RETIENT_RIEN
        )}
        {' · '}
        {AU_PLUS_TOT_DU_PLAN} : <b>{dateDuPlan(bulle.dateAuPlusTot)}</b>
      </p>
    </section>
  );
}

/** Une colonne du schéma : son titre, trois bulles, puis « + N autres », qui déplie. */
function ColonneDuPlan({ titre, noeuds }: { titre: string; noeuds: NoeudDuPlan[] }) {
  const [toutes, setToutes] = useState(false);
  const montrees = toutes ? noeuds : noeuds.slice(0, MONTREES);
  const cachees = noeuds.length - montrees.length;
  return (
    <div className="plan-colonne">
      <h5 className="plan-colonne-titre">{titre}</h5>
      {montrees.map((n) => (
        <div key={n.cle} className={`plan-noeud${n.acquise ? ' plan-noeud--acquise' : ''}`}>
          {n.titre}
          {n.acquise && ' ✓'}
          {n.mention && (
            <>
              <br />
              <small>{n.mention}</small>
            </>
          )}
        </div>
      ))}
      {cachees > 0 && (
        <button type="button" className="plan-autres" onClick={() => setToutes(true)}>
          {nAutres(cachees)}
        </button>
      )}
    </div>
  );
}
